// ============================================================================
// CONFIGURATION TEMPLATE MANAGER
// ============================================================================

import { API_BASE_URL } from '@types';
import type { SessionConfig } from '@pages/sessions/new/utils/experienceForm/transform';

// ============================================================================
// TYPES
// ============================================================================

export interface ConfigurationTemplate {
  id: number;
  name: string;
  title?: string;
  description?: string;
  shortDescription?: string;
  sessionType: 'scenario' | 'esperienza';
  steps: SessionConfig['steps'];
  createdAt?: string;
  updatedAt?: string;
  userId: string;
}

export interface ConfigurationTemplateResponse {
  data: ConfigurationTemplate;
}

export interface CreateConfigurationTemplateRequest {
  userId: string;
  name: string;
  title?: string;
  description?: string;
  shortDescription?: string;
  sessionType: 'scenario' | 'esperienza';
  config: SessionConfig;
}

export type UpdateConfigurationTemplateRequest =
  Partial<CreateConfigurationTemplateRequest>;

/**
 * Request to create a session configuration (not a template)
 * This is used when saving an actual session configuration
 */
export interface CreateSessionConfigurationRequest {
  name: string;
  description?: string;
  userId: string | number;
  patientId: string | number;
  deviceId: string | number;
  sessionType: 'scenario' | 'esperienza';
  config: SessionConfig;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Map database response (snake_case) to TypeScript interface (camelCase)
 */
const mapTemplateFromDB = (item: any): any => {
  const mapped: any = {
    ...item,
    shortDescription: item.short_description || item.shortDescription,
  };

  // Remove snake_case field if it exists (to avoid confusion)
  if (item.short_description) {
    delete mapped.short_description;
  }

  return mapped;
};

/**
 * Deserialize config from JSON string (from database) to SessionConfig object
 * Steps are parsed from JSON string to array
 */
const deserializeConfigFromDB = (
  configString: string | SessionConfig
): SessionConfig => {
  // Se è già un oggetto, verifica se steps è stringa
  if (typeof configString !== 'string') {
    if (configString && typeof configString.steps === 'string') {
      try {
        return {
          ...configString,
          steps: JSON.parse(configString.steps),
        };
      } catch (error) {
        console.error('Error parsing config steps:', error);
        return configString;
      }
    }
    return configString;
  }

  // Se è una stringa, parsala
  try {
    const parsed = JSON.parse(configString);
    // Se steps è una stringa dentro il config parsato, parsala
    if (parsed && typeof parsed.steps === 'string') {
      return {
        ...parsed,
        steps: JSON.parse(parsed.steps),
      };
    }
    return parsed;
  } catch (error) {
    console.error('Error parsing config from DB:', error);
    throw error;
  }
};

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Get all configuration templates
 */
export const getConfigurationTemplates =
  async (): Promise<ConfigurationTemplateResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/services/v1/configurations/templates`,
      {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();

    // Parse config from JSON string to object and map snake_case to camelCase
    if (result.data) {
      if (Array.isArray(result.data)) {
        result.data = result.data.map((item: any) => {
          const mapped = mapTemplateFromDB(item);
          return {
            ...mapped,
            config: deserializeConfigFromDB(mapped.config),
          };
        });
      } else {
        const mapped = mapTemplateFromDB(result.data);
        result.data = {
          ...mapped,
          config: deserializeConfigFromDB(mapped.config),
        };
      }
    }

    return result;
  };

/**
 * Get a single configuration template by ID
 */
export const getConfigurationTemplate = async (
  id: string | number
): Promise<ConfigurationTemplateResponse | null> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/configurations/templates/${id}`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const result = await response.json();

  // Parse config from JSON string to object and map snake_case to camelCase
  if (result.data) {
    const data: ConfigurationTemplate = result.data;
    return {
      data: {
        id: data.id,
        name: data.name,
        description: data.description,
        steps: data.steps,
        sessionType: data.sessionType,
        userId: data.userId,
      },
    };
  }
  return null;
};

/**
 * Create a new configuration template
 */
export const createConfigurationTemplate = async (
  data: CreateConfigurationTemplateRequest
): Promise<ConfigurationTemplateResponse | null> => {
  // Serialize only config to minified JSON string for DB
  const payload = {
    name: data.name,
    description: data.description,
    steps: JSON.stringify(data.config.steps),
    userId: data.userId,
  };

  const response = await fetch(
    `${API_BASE_URL}/services/v1/configurations/templates`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const result: ConfigurationTemplateResponse = await response.json();

  // Parse config from JSON string to object in response and map snake_case to camelCase
  if (result.data) {
    const data: ConfigurationTemplate = result.data;
    return {
      data: {
        id: data.id,
        name: data.name,
        description: data.description,
        steps: data.steps,
        sessionType: data.sessionType,
        userId: data.userId,
      },
    };
  }
  return null;
};

/**
 * Update an existing configuration template
 */
export const updateConfigurationTemplate = async (
  id: string | number,
  data: UpdateConfigurationTemplateRequest
): Promise<ConfigurationTemplateResponse> => {
  // Build payload with only fields to update, serialize config if provided
  const payload = {
    ...(data.userId !== undefined && { userId: data.userId }),
    ...(data.name !== undefined && { name: data.name }),
    ...(data.description !== undefined && { description: data.description }),
    ...(data.sessionType !== undefined && { sessionType: data.sessionType }),
    ...(data.config && { config: JSON.stringify(data.config.steps) }),
  };

  const response = await fetch(
    `${API_BASE_URL}/services/v1/configurations/templates/${id}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const result = await response.json();

  // Parse config from JSON string to object in response and map snake_case to camelCase
  if (result.data) {
    if (Array.isArray(result.data)) {
      result.data = result.data.map((item: any) => {
        const mapped = mapTemplateFromDB(item);
        return {
          ...mapped,
          config: deserializeConfigFromDB(mapped.config),
        };
      });
    } else {
      const mapped = mapTemplateFromDB(result.data);
      result.data = {
        ...mapped,
        config: deserializeConfigFromDB(mapped.config),
      };
    }
  }

  return result;
};

/**
 * Delete a configuration template
 */
export const deleteConfigurationTemplate = async (
  id: string | number
): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/configurations/templates/${id}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
};

// ============================================================================
// SAFE FUNCTIONS (with error handling)
// ============================================================================

/**
 * Get all configuration templates safely (returns empty array on error)
 */
export const getConfigurationTemplatesSafe = async (): Promise<
  ConfigurationTemplate[]
> => {
  try {
    const response = await getConfigurationTemplates();
    const data = response.data;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching configuration templates:', error);
    return [];
  }
};

/**
 * Get a single configuration template safely (returns null on error)
 */
export const getConfigurationTemplateSafe = async (
  id: string | number
): Promise<ConfigurationTemplate | null> => {
  try {
    const response = await getConfigurationTemplate(id);
    const data = response?.data;
    return Array.isArray(data) ? data[0] || null : data || null;
  } catch (error) {
    console.error(`Error fetching configuration template ${id}:`, error);
    return null;
  }
};

/**
 * Create a configuration template safely (returns null on error)
 */
export const createConfigurationTemplateSafe = async (
  data: CreateConfigurationTemplateRequest
): Promise<ConfigurationTemplate | null> => {
  try {
    const response = await createConfigurationTemplate(data);
    const templateData = response?.data;
    return Array.isArray(templateData)
      ? templateData[0] || null
      : templateData || null;
  } catch (error) {
    console.error('Error creating configuration template:', error);
    throw error; // Re-throw to allow error handling in components
  }
};

/**
 * Update a configuration template safely (returns null on error)
 */
export const updateConfigurationTemplateSafe = async (
  id: string | number,
  data: UpdateConfigurationTemplateRequest
): Promise<ConfigurationTemplate | null> => {
  try {
    const response = await updateConfigurationTemplate(id, data);
    const templateData = response.data;
    return Array.isArray(templateData)
      ? templateData[0] || null
      : templateData || null;
  } catch (error) {
    console.error(`Error updating configuration template ${id}:`, error);
    throw error; // Re-throw to allow error handling in components
  }
};

/**
 * Delete a configuration template safely (returns false on error)
 */
export const deleteConfigurationTemplateSafe = async (
  id: string | number
): Promise<boolean> => {
  try {
    await deleteConfigurationTemplate(id);
    return true;
  } catch (error) {
    console.error(`Error deleting configuration template ${id}:`, error);
    return false;
  }
};

/**
 * Create a session configuration (not a template)
 * This saves an actual session configuration with patient and device
 */
export const createSessionConfiguration = async (
  data: CreateSessionConfigurationRequest
): Promise<ConfigurationTemplateResponse> => {
  // Serialize only config to minified JSON string for DB
  // Other fields (name, description, sessionType, patientId, deviceId) are separate fields
  const payload = {
    name: data.name,
    description: data.description,
    userId: data.userId,
    patientId: data.patientId,
    deviceId: data.deviceId,
    sessionType: data.sessionType,
    config: JSON.stringify(data.config),
    steps: JSON.stringify(data.config.steps),
  };

  const response = await fetch(
    `${API_BASE_URL}/services/v1/configurations/templates`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const result = await response.json();

  // Parse config from JSON string to object in response and map snake_case to camelCase
  if (result.data) {
    if (Array.isArray(result.data)) {
      result.data = result.data.map((item: any) => {
        const mapped = mapTemplateFromDB(item);
        return {
          ...mapped,
          config: deserializeConfigFromDB(mapped.config),
        };
      });
    } else {
      const mapped = mapTemplateFromDB(result.data);
      result.data = {
        ...mapped,
        config: deserializeConfigFromDB(mapped.config),
      };
    }
  }

  return result;
};

/**
 * Create a session configuration safely (returns null on error)
 */
export const createSessionConfigurationSafe = async (
  data: CreateSessionConfigurationRequest
): Promise<ConfigurationTemplate | null> => {
  try {
    const response = await createSessionConfiguration(data);
    const templateData = response.data;
    return Array.isArray(templateData)
      ? templateData[0] || null
      : templateData || null;
  } catch (error) {
    console.error('Error creating session configuration:', error);
    throw error; // Re-throw to allow error handling in components
  }
};
